import { useContext } from 'react'
import type { ReactElement } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { StoresContext } from '../../provider/StoreProvider'

import { useNavigate } from '../../hooks/useNavigate'
import { useIsMobile } from '../../hooks/useIsMobile'

import { Text } from '../text'
import type { TextProps } from '../text'

import { Color } from '../../theme/color'

import { ReactComponent as ContentIcon } from '../../assets/ico/footer/content/content_default_28.svg'
import { ReactComponent as ContentActiveIcon } from '../../assets/ico/footer/content/content_active_28.svg'
import { ReactComponent as MarketIcon } from '../../assets/ico/footer/market/market_default_28.svg'
import { ReactComponent as MarketActiveIcon } from '../../assets/ico/footer/market/market_active_28.svg'
import { ReactComponent as HomeIcon } from '../../assets/ico/footer/home/home_default_28.svg'
import { ReactComponent as HomeActiveIcon } from '../../assets/ico/footer/home/home_active_28.svg'
import { ReactComponent as MoreIcon } from '../../assets/ico/footer/more/more_default_28.svg'
import { ReactComponent as MoreActiveIcon } from '../../assets/ico/footer/more/more_active_28.svg'
import { ReactComponent as InvestmentIcon } from '../../assets/ico/footer/investment/investment_default_28.svg'
import { ReactComponent as InvestmentActiveIcon } from '../../assets/ico/footer/investment/investment_active_28.svg'

type Tab = 'home' | 'market' | 'contents' | 'myPage' | 'setting'

interface NavigateTabInfo {
  id: Tab
  title: string
  activeIcon: ReactElement
  icon: ReactElement
}

const NAVIGATE_TABS: NavigateTabInfo[] = [
  {
    id: 'home',
    title: '홈',
    activeIcon: <HomeActiveIcon />,
    icon: <HomeIcon />,
  },
  {
    id: 'market',
    title: '청약',
    activeIcon: <MarketActiveIcon />,
    icon: <MarketIcon />,
  },
  {
    id: 'contents',
    title: '콘텐츠',
    activeIcon: <ContentActiveIcon />,
    icon: <ContentIcon />,
  },
  {
    id: 'myPage',
    title: '투자 내역',
    activeIcon: <InvestmentActiveIcon />,
    icon: <InvestmentIcon />,
  },
  {
    id: 'setting',
    title: '더보기',
    activeIcon: <MoreActiveIcon />,
    icon: <MoreIcon />,
  },
]

export const NavigateTab = observer(() => {
  const {
    commonStore: { isShowTab, activeTab, setActiveTab },
  } = useContext(StoresContext)

  const navigate = useNavigate()
  const isMobile = useIsMobile()

  if (!isShowTab) return null

  const textProps: TextProps = { size: isMobile ? 10 : 14, ...(isMobile ? undefined : { weight: 500, lineHeight: 20 }) }

  const handleNavigate = (tab: string) => {
    setActiveTab(tab)
    navigate(`/${tab}`)
  }

  return (
    <Container isMobile={isMobile}>
      {NAVIGATE_TABS.map(({ id, title, activeIcon, icon }) => {
        const isActive = activeTab === id

        const clickNavigate = () => {
          handleNavigate(id)
        }

        return (
          <Button key={id} onClick={clickNavigate}>
            {isActive ? activeIcon : icon}
            <Text {...textProps} color={isActive ? 'gray_8' : 'gray_6'}>
              {title}
            </Text>
          </Button>
        )
      })}
    </Container>
  )
})

const Container = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  width: 100%;
  border-top: 1px solid ${Color.line};
  background-color: ${Color.white};
  ${({ isMobile }) =>
    isMobile
      ? ''
      : `> * {
          padding-bottom: 24px;
        }`}
`

const Button = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding-top: 12px;
  cursor: pointer;
`
